import Table from "../ui/categoryTable/TableController";
import { useSelector, useDispatch } from "react-redux";
import { uiSelector } from "../../redux/slices/uiSlice";
import {
  companiesSelector,
  updateCategory,
  updateCategoryState,
} from "../../redux/slices/companiesSlice";
import Loading from "./tableState/Loading";
import NoSearch from "./tableState/NoSearch";
import NoItems from "./tableState/NoItems";
import { authSelector } from "../../redux/slices/authSlice";
import {
  getItemizerCodesForToken,
  getIndexedFieldName,
  isSpecialItemizerLogin,
} from "../../utils/itemizerRules";

const POS_SECTION = {
  background: "#eef4fd",
  color: "#346fcf",
  dividerColor: "#88aef5",
};

const PMS_SECTION = {
  background: "#f4effb",
  color: "#7b68b3",
  dividerColor: "#88aef5",
};

const Category = () => {
  const {
    categories,
    itemizers,
    categoryItemizersSlg,
    categoryItemizersByName,
    categoryItemizersLoading,
    tableLoading,
  } = useSelector(companiesSelector);
  const dispatch = useDispatch();
  const { searchQuery } = useSelector(uiSelector);
  const { token } = useSelector(authSelector);
  const showSpecialItemizerColumns = isSpecialItemizerLogin({ token });
  const itemizerCodes = getItemizerCodesForToken({ token });
  const defaultItemizerOptions = (
    categoryItemizersSlg.length ? categoryItemizersSlg : itemizers
  ).map((i) => ({ label: i.name, value: i.id }));
  const pmsColumns = showSpecialItemizerColumns
    ? itemizerCodes.map((code, index) => ({
        code,
        field: getIndexedFieldName("pms_name", index),
        idField: getIndexedFieldName("pms_id", index),
        label: `PMS Name (${code})`,
        options: (categoryItemizersByName[code] || []).map((i) => ({
          label: i.name,
          value: i.id,
        })),
      }))
    : [
        {
          field: "pms_name",
          idField: "pms_id",
          label: "PMS Name",
          options: defaultItemizerOptions,
        },
        {
          field: "pms_name1",
          idField: "pms_id1",
          label: "PMS Name (Lunch)",
          options: defaultItemizerOptions,
        },
        {
          field: "pms_name2",
          idField: "pms_id2",
          label: "PMS Name (Dinner)",
          options: defaultItemizerOptions,
        },
      ];

  const filteredCategories = categories.filter((i) => {
    const search = searchQuery.toLowerCase();

    return (
      i.category_name?.toLowerCase().includes(search) ||
      String(i.category_id || "").includes(search) ||
      pmsColumns.some((column) => i[column.field]?.toLowerCase().includes(search)) ||
      String(i.pms_id || "").includes(search) ||
      i.category_type?.toLowerCase().includes(search)
    );
  });

  const sectionHeaders = [
    {
      id: "pos",
      label: "POS",
      span: 3,
      background: POS_SECTION.background,
      color: POS_SECTION.color,
      dividerAfter: true,
      dividerColor: POS_SECTION.dividerColor,
    },
    {
      id: "pms",
      label: "PMS",
      span: pmsColumns.length + 3,
      background: PMS_SECTION.background,
      color: PMS_SECTION.color,
    },
  ];

  const pmsHeaderData = pmsColumns.map((column, index) => ({
    id: 422 + index,
    label: column.label,
    width: showSpecialItemizerColumns ? "10%" : "14%",
    sortable: true,
    section: "pms",
    sectionBackground: PMS_SECTION.background,
  }));

  const headerData = [
    {
      id: 122,
      label: "Company",
      width: "14%",
      sortable: false,
      section: "pos",
      sectionBackground: POS_SECTION.background,
    },
    {
      id: 222,
      label: "Name",
      width: "14%",
      sortable: true,
      section: "pos",
      sectionBackground: POS_SECTION.background,
    },
    {
      id: 322,
      label: "Id",
      width: "12%",
      sortable: true,
      section: "pos",
      sectionBackground: POS_SECTION.background,
      dividerAfter: true,
      dividerColor: POS_SECTION.dividerColor,
    },
    ...pmsHeaderData,
    {
      id: 522,
      label: "Category Type",
      width: "9%",
      sortable: true,
      section: "pms",
      sectionBackground: PMS_SECTION.background,
    },
    {
      id: 523,
      label: "Taxes by Category",
      width: "11%",
      sortable: false,
      section: "pms",
      sectionBackground: PMS_SECTION.background,
    },
    {
      id: 622,
      label: "Action",
      width: "7%",
      sortable: false,
      section: "pms",
      sectionBackground: PMS_SECTION.background,
    },
  ];

  const rowKeys = [
    "company_id",
    "category_name",
    "category_id",
    ...pmsColumns.map((column) => column.field),
    "category_type",
  ];

  const editable = [...pmsColumns.map((column) => column.field), "category_type"];

  const editableOptions = {
    ...pmsColumns.reduce(
      (options, column) => ({
        ...options,
        [column.field]: column.options,
      }),
      {}
    ),
    category_type: [
      { value: "sale", label: "sale" },
      { value: "discount", label: "discount" },
      { value: "svc", label: "svc" },
    ],
  };

  const sortByData = [
    {},
    {
      label: "category_name",
      type: "string",
    },
    {
      label: "category_id",
      type: "number",
    },
    ...pmsColumns.map((column) => ({
      label: column.field,
      type: "string",
    })),
    {
      label: "category_type",
      type: "string",
    },
    {},
    {},
  ];

  const updateAction = (x) => {
    dispatch(updateCategoryState(x));
    const updatePayload = { ...x };

    if (showSpecialItemizerColumns) {
      delete updatePayload.pms_id_deferred;
    }

    return dispatch(updateCategory(updatePayload));
  };

  if (tableLoading || (showSpecialItemizerColumns && categoryItemizersLoading)) {
    return <Loading />;
  }

  if (filteredCategories.length === 0 && searchQuery) return <NoSearch />;

  if (filteredCategories.length === 0) return <NoItems />;

  return (
    <Table
      headerData={headerData}
      sectionHeaders={sectionHeaders}
      rowKeys={rowKeys}
      rowKey="category_id"
      editableOptions={editableOptions}
      taxCategoryOptions={editableOptions[pmsColumns[0].field] || []}
      editableItemizerNames={
        showSpecialItemizerColumns
          ? pmsColumns.reduce(
              (names, column) => ({ ...names, [column.field]: column.code }),
              {}
            )
          : {}
      }
      editable={editable}
      sortByData={sortByData}
      data={filteredCategories}
      updateAction={updateAction}
    />
  );
};

export default Category;



